import { Auth0Provider } from '@auth0/auth0-react';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import React, { ReactNode, Suspense, lazy, useMemo } from 'react';
import auth0Config from '~/helpers/auth0Config';
import { getCookieValue } from '~/helpers/utils';
import { Auth0AuthProvider } from './Auth0AuthProvider';
import { useShouldUseSvcOnboardingAuth } from './useShouldUseSvcOnboardingAuth';

const CognitoAuthProvider = lazy(() =>
  import('./CognitoAuthProvider').then((m) => ({
    default: m.CognitoAuthProvider,
  }))
);

const SvcOnboardingAuthProvider = lazy(() =>
  import('./SvcOnboardingAuthProvider').then((m) => ({
    default: m.SvcOnboardingAuthProvider,
  }))
);

export { CognitoAuthEvent, useAuth } from './AuthContext';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const isInternalProd = import.meta.env.MODE === 'internal-prod';
  const shouldUseCognito =
    useFeatureFlagEnabled('cognito-authentication') && !isInternalProd;

  const shouldUseSvcOnboarding = useShouldUseSvcOnboardingAuth();

  const hasIsAuthenticatedCookie = useMemo(() => {
    const auth0IsAuthenticatedCookie = getCookieValue(
      `auth0.${import.meta.env.VITE_AUTH0_CLIENT_ID}.is.authenticated`
    );

    return auth0IsAuthenticatedCookie === 'true';
  }, []);

  if (shouldUseSvcOnboarding) {
    return (
      <Suspense>
        <SvcOnboardingAuthProvider>{children}</SvcOnboardingAuthProvider>
      </Suspense>
    );
  }

  if (shouldUseCognito) {
    return (
      <Suspense>
        <CognitoAuthProvider>{children}</CognitoAuthProvider>
      </Suspense>
    );
  }

  return (
    <Auth0Provider {...auth0Config}>
      <Auth0AuthProvider hasIsAuthenticatedCookie={hasIsAuthenticatedCookie}>
        {children}
      </Auth0AuthProvider>
    </Auth0Provider>
  );
};
